import React, { Component } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";

// Global Error Boundary - prevents blank screens from unhandled render errors
class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] React render crash:', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0a0f',
          color: '#fff',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          padding: '24px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '400px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ fontSize: '20px', margin: '0 0 12px', color: '#ef4444' }}>Something went wrong</h2>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: '0 0 24px', lineHeight: '1.5' }}>
              The app encountered an unexpected error. Please refresh to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 32px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Refresh App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// BOOT INSTRUMENTATION - Track app startup for debugging blank screens
// ═══════════════════════════════════════════════════════════════════════════════
console.log('[BOOT] App boot start', { 
  timestamp: new Date().toISOString(),
  url: window.location.href,
  userAgent: navigator.userAgent.substring(0, 100)
});

// Global error handler for uncaught errors
window.onerror = (message, source, lineno, colno, error) => {
  console.error('[BOOT] Uncaught error:', { message, source, lineno, colno, error: error?.stack });
  // Try to log to server
  try {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    if (BACKEND_URL) {
      fetch(`${BACKEND_URL}/api/client-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'uncaught_error',
          message: String(message),
          source,
          lineno,
          colno,
          stack: error?.stack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {}); // Fire and forget
    }
  } catch (e) {
    // Ignore logging errors
  }
};

// Global promise rejection handler
window.onunhandledrejection = (event) => {
  console.error('[BOOT] Unhandled promise rejection:', event.reason);
  try {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    if (BACKEND_URL) {
      fetch(`${BACKEND_URL}/api/client-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'unhandled_rejection',
          message: String(event.reason),
          stack: event.reason?.stack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {});
    }
  } catch (e) {
    // Ignore
  }
};

console.log('[BOOT] Config loaded', { 
  BACKEND_URL: process.env.REACT_APP_BACKEND_URL || 'NOT SET'
});

const root = ReactDOM.createRoot(document.getElementById("root"));
console.log('[BOOT] React root created');

root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>,
);

console.log('[BOOT] React render called');
